'use client';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  getAuth,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithCredential,
  signInAnonymously,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { initUserAssetsEndpoint, initUserEndpoint } from '@/config/endpoints';
import { useGoogleOneTapLogin } from '@react-oauth/google';
import { app } from '@/config/firebaseConfig';
import { kazeFetch } from '@/utils/kazeFetch';
import { reportLogin } from '@/posthog/reportLogin';
import { useInterval } from 'react-use';
import { updateSentryUser } from '@/utils/sentry';
import { getInviteInfo } from '@/ueshook/useInviteCode';
import { ImageUploadMonitor } from '@/monitor/image_upload_context';
import { feBot } from '@/monitor';
import tryCatchPromise from '@/utils/tryCatchPromise';
import { ImgUploadRP } from '@/service/uploadImgService/RP';

const { invite } = getInviteInfo();

declare global {
  interface Window {
    google?: { accounts: { id: { cancel: () => void } } };
  }
}

export interface UserInfoAssets {
  user_id: string;
  email: string | null;
  ever_paid: boolean; // 是否付费过
  is_anonymous: boolean; // 是否为匿名用户
  hd_download_account: number; // 下载次数
  plan: {
    total_limit: number; // 目前总上限
    available_count: number; // 目前可用量
    usage_count: number; // 目前已消耗量
    lifetime_limit: number; // 不会过期的 limit
    expire_limit: number; // 会过期的 limit ，不存在为 0
    expire_at: number | null; // 会过期 limit 的过期时间，Optional 表示可能为 null
    id_photo: {
      basic_limit: number;
      basic_usage: number;
      pro_limit: number;
      pro_usage: number;
    };
  };
  registered_at: number;
  subscription: {
    in_subscription: boolean; // 是否处在某个订阅计划中（false 可能是从未订阅或曾经订阅但取消了）
    subscription_name: string; // 订阅计划名字 -
    start_at: number; // 订阅开始时间
    end_at: number; // 订阅结束时间
  };
}
/**
 * @deprecated 将来从全局移除
 */
export interface UserCacheData {
  repairInputImg: string | null;
  repairOutputImg: string | null;
  repairOutputThumbnail: string | null;
}

export interface UserInfoContextType {
  /**
   * @deprecated authToken 将来会从全局移除 - 使用getAuthToken保证及时更新
   */
  authToken: string | undefined;
  /**
   * @deprecated userCacheData 将来从全局移除
   */
  userCacheData: UserCacheData;
  /**
   * @deprecated updateUserCacheData 将来从全局移除
   */
  updateUserCacheData: (data: {
    [K in keyof UserCacheData]?: UserCacheData[K];
  }) => Promise<void>;

  userIdentity: 1 | 2 | 3; // 1-非用户;2-匿名用户;3-登录用户
  setUserIdentity: (userIdentity: UserInfoContextType['userIdentity']) => void; // Add setter for immediate updates
  signInAnonymous: (_from: string) => Promise<{ authToken: string | null }>;
  userInfo: UserInfoAssets | null;
  isLoadingUser: boolean;
  updateUserInfoAssets: () => Promise<void>;
  updateUserInfo: () => Promise<void>;
  getAuthToken: () => Promise<string | null>;
}

const UserInfoContext = createContext<UserInfoContextType>({
  authToken: undefined,
  userIdentity: 1,
  setUserIdentity: () => {},
  signInAnonymous: async (_from: string) => {
    return { authToken: null };
  },
  userInfo: null,
  /**
   * @deprecated userCacheData 将来从全局移除
   */
  userCacheData: {
    repairInputImg: null,
    repairOutputImg: null,
    repairOutputThumbnail: null,
  },
  isLoadingUser: true,
  updateUserInfoAssets: async () => {},
  updateUserInfo: async () => {},
  updateUserCacheData: async () => {},
  getAuthToken: async () => null,
});

// 定义一个简单的 Promise 缓存
interface CachedPromise<T> {
  status: 'pending' | 'success' | 'error';
  value?: T;
  error?: any;
}

// 创建一个 token 缓存
const tokenCache: CachedPromise<string | null> = {
  status: 'pending',
  value: undefined,
  error: undefined,
};

export function UserInfoProvider({ children }: { children: React.ReactNode }) {
  const [userInfo, setUserInfo] = useState<UserInfoAssets | null>(null);
  const [userCacheData, setUserCacheData] = useState<UserCacheData>({
    repairInputImg: null,
    repairOutputImg: null,
    repairOutputThumbnail: null,
  });
  const [userIdentity, setUserIdentity] = useState<1 | 2 | 3>(1);
  const [authToken, setAuthToken] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  // 匿名登录时上锁，保证接口通信完成前不再触发监听登录change
  const anonymousLoginLock = useRef(false);
  // 用于跟踪匿名登录的 Promise
  const anonymousLoginPromise = useRef<Promise<{
    authToken: string | null;
  }> | null>(null);
  // 执行以停止监听用户变化onSnapshot
  const offSnapshot = useRef(() => {});

  const resetTokenCache = useCallback(() => {
    tokenCache.status = 'pending';
    tokenCache.value = undefined;
    tokenCache.error = undefined;
  }, []);

  // 每 10 分钟重置一次 tokenCache
  useInterval(resetTokenCache, 10 * 60 * 1000);

  const getAuthToken = useCallback(async () => {
    // 如果已经成功获取过，直接返回缓存的值，确保返回 null 而不是 undefined

    if (tokenCache.status === 'success') {
      if (tokenCache.value) {
        return tokenCache.value;
      } else {
        throw new Error('tokenCache.value is undefined');
      }
    }

    // 如果有错误，返回 null
    if (tokenCache.status === 'error') {
      throw tokenCache.error;
    }

    try {
      const nowAuth = getAuth(app);
      await nowAuth.authStateReady();
      const token = nowAuth.currentUser
        ? await nowAuth.currentUser.getIdToken()
        : null;
      if (!token) return null;
      tokenCache.status = 'success';
      tokenCache.value = token;
      return token;
    } catch (error) {
      tokenCache.status = 'error';
      tokenCache.error = error;
      throw error;
    }
  }, []);

  const signInAnonymous: (_from: string) => Promise<{
    authToken: string | null;
  }> = async (_from) => {
    // 如果正在匿名登录，返回现有的 Promise
    if (anonymousLoginLock.current && anonymousLoginPromise.current)
      return anonymousLoginPromise.current;

    // 创建新的登录 Promise
    anonymousLoginPromise.current = (async () => {
      anonymousLoginLock.current = true;
      offSnapshot.current?.();

      setIsLoading(true);
      const nowAuth = getAuth(app);

      try {
        // 增强持久化设置
        await setPersistence(nowAuth, browserLocalPersistence);
      } catch (error) {
        console.error('Persistence setup failed:', error);
        anonymousLoginLock.current = false;
        anonymousLoginPromise.current = null;
        return { authToken: null };
      }

      // 先检查现有登录状态
      await nowAuth.authStateReady();
      const currentUser = nowAuth.currentUser;

      // 如果已有匿名用户且token未过期，直接使用
      if (currentUser?.isAnonymous) {
        console.log('发现已存在的匿名用户');
        const authToken = await currentUser.getIdToken();
        setAuthToken(authToken);
        anonymousLoginLock.current = false;
        anonymousLoginPromise.current = null;
        await updateUserInfo();
        return { authToken };
      }

      console.log('没有存储的用户信息，尝试匿名登录');
      const userCredential = await signInAnonymously(nowAuth);
      const authToken = await userCredential.user?.getIdToken();
      setAuthToken(authToken);

      try {
        const res = await kazeFetch(initUserEndpoint, { method: 'POST' });
        console.log('Anonymous user initialized successfully', res);
        await updateUserInfo();
        return { authToken };
      } catch (error) {
        console.error('Failed to initialize Anonymous user:', error);
        return { authToken: null };
      } finally {
        anonymousLoginLock.current = false;
        anonymousLoginPromise.current = null;
      }
    })();

    return anonymousLoginPromise.current;
  };

  /**
   * @description 更新用户信息
   */
  const updateUserInfo = useCallback(async () => {
    const nowAuth = getAuth(app);
    const db = getFirestore();

    try {
      await nowAuth.authStateReady();
      const currentUser = nowAuth.currentUser;
      if (currentUser) {
        setUserIdentity(currentUser.isAnonymous ? 2 : 3);
        const userDbIndex = doc(db, 'users', currentUser.uid);
        const { data: docSnap, error } = await tryCatchPromise(
          getDoc(userDbIndex)
        );
        if (error) {
          console.error(`updateUserInfo error:`, error);
          setUserInfo(null);
          setIsLoading(false);
          return;
        }
        if (docSnap.exists()) {
          const authToken = await nowAuth.currentUser?.getIdToken();
          setAuthToken(authToken);
          const userInfo = await kazeFetch(initUserAssetsEndpoint, {
            method: 'POST',
          });
          setUserInfo(userInfo);

          // TODO 更新一些类的用户信息，可以做成发布订阅模式
          ImageUploadMonitor.setUserId(userInfo.user_id);
          feBot.setUserId(userInfo.user_id);
          feBot.setEmail(userInfo.email);
          feBot.setUserIdentity(currentUser.isAnonymous ? 2 : 3);
          ImgUploadRP.setUserId(userInfo.user_id);
          ImgUploadRP.setEmail(userInfo.email);
          ImgUploadRP.setUserIdentity(currentUser.isAnonymous ? 2 : 3);

          console.log(`updateUserInfo success!`);
        }
        setIsLoading(false);
      } else {
        console.log(`updateUserInfo, there is no user`);
        setUserInfo(null);
        setUserIdentity(1);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('updateUserInfo error:', error);
      setIsLoading(false);
    }
  }, []);

  /**
   * @description 下载/订阅/消费 之后更新
   */
  const updateUserInfoAssets = useCallback(async () => {
    try {
      if (userIdentity === 1) {
        throw Error('非用户禁止获取资产！');
      }
      const nowAuth = getAuth(app);
      const authToken = await nowAuth.currentUser?.getIdToken();
      setAuthToken(authToken);
      const userInfo = await kazeFetch(initUserAssetsEndpoint, {
        method: 'POST',
      });
      setUserInfo(userInfo);
    } catch (error) {
      console.error('updateUserInfo error:', error);
    }
  }, [userIdentity]);

  /**
   * @description 去水印/图像修复时，更新缓存数据
   */
  const updateUserCacheData = useCallback(
    async (data: { [K in keyof UserCacheData]?: UserCacheData[K] }) => {
      try {
        setUserCacheData({ ...userCacheData, ...data });
      } catch (error) {
        console.error('updateUserCacheData error:', error);
      }
    },
    [userCacheData]
  );

  useEffect(() => {
    // 初始化时立即检查持久化状态
    const checkPersistedAuth = async () => {
      const nowAuth = getAuth(app);
      await setPersistence(nowAuth, browserLocalPersistence);
      await nowAuth.authStateReady();
    };
    checkPersistedAuth();

    // Listen to auth changes
    const unsubscribeAuth = onAuthStateChanged(getAuth(app), async (user) => {
      // const token = await user?.getIdToken();
      // setAuthToken(token);
      // 更新 Sentry 用户信息
      updateSentryUser();
      // 当用户登录态改变时，重置 tokenCache
      resetTokenCache();
      // 正在匿名登录时禁止重复触发
      if (anonymousLoginLock.current) {
        return;
      }
      await updateUserInfo();
    });

    // Cleanup
    return () => {
      unsubscribeAuth();
    };
  }, [resetTokenCache, updateUserInfo]);

  useGoogleOneTapLogin({
    onSuccess: async (credentialResponse) => {
      const credential = GoogleAuthProvider.credential(
        credentialResponse.credential
      );

      await signInWithCredential(getAuth(app), credential)
        .then(async (userCredential) => {
          const authToken = await userCredential.user?.getIdToken();
          setAuthToken(authToken);
          const res = await kazeFetch(initUserEndpoint, {
            method: 'POST',
            body: JSON.stringify({
              invite_code: invite.invite_code || null,
            }),
          });
          console.log('Google one-tap user initialized successfully!', res);
          // 上报
          reportLogin(userCredential.user, 'one-tap');
        })
        .catch(console.error);
    },
    onError: () => {
      console.error('Login OneTap Google Failed');
    },
    cancel_on_tap_outside: false, // 点击外部取消
    // 加载用户信息时；有用户信息时；匿名登录时都不显示一键登录
    disabled: isLoading || !!userInfo || anonymousLoginLock.current,
    use_fedcm_for_prompt: true,
  });

  useEffect(() => {
    // 当用户信息加载完成时，取消 google one tap 弹窗
    if (window.google?.accounts?.id && userInfo !== null) {
      try {
        window.google.accounts.id.cancel();
      } catch (error) {
        console.error('Failed to cancel Google OneTap:', error);
      }
    }
  }, [userInfo]);

  return (
    <UserInfoContext.Provider
      value={{
        userCacheData,
        userInfo,
        isLoadingUser: isLoading,
        userIdentity,
        authToken,
        setUserIdentity,
        signInAnonymous,
        updateUserInfoAssets,
        updateUserInfo,
        updateUserCacheData,
        getAuthToken,
      }}
    >
      {children}
    </UserInfoContext.Provider>
  );
}

// Simplify the hook to only return context
export function useUserInfo(): UserInfoContextType {
  const context = useContext(UserInfoContext);
  if (!context) {
    throw new Error('useUserInfo must be used within UserInfoProvider');
  }
  return context;
}
